import { Trans, useLingui } from '@lingui/react/macro'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'designSystem/components/AlertDialog'
import { Text } from 'designSystem/components/Text'
import type { LotoLeavePrompt } from 'features/lotoReports/useLotoLeavePrompt'
import type { MouseEvent, ReactElement } from 'react'

export function LotoLeaveDialog({
  prompt,
}: {
  prompt: LotoLeavePrompt
}): ReactElement {
  const { t } = useLingui()
  const {
    open,
    reportName,
    saving,
    saveError,
    saveAndContinue,
    discardAndContinue,
    cancel,
  } = prompt
  const changeOpen = (value: boolean): void => {
    if (!value && !saving) cancel()
  }
  const save = (event: MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault()
    void saveAndContinue()
  }
  const discard = (event: MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault()
    discardAndContinue()
  }
  const name = reportName ?? t`this LOTO`
  const errorMessage = saveError?.message
  return (
    <AlertDialog open={open} onOpenChange={changeOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <Trans>Save changes to {name}?</Trans>
          </AlertDialogTitle>
          <AlertDialogDescription>
            <Trans>
              This LOTO has unsaved changes. Save them before you continue, or
              discard them.
            </Trans>
          </AlertDialogDescription>
        </AlertDialogHeader>
        {errorMessage !== undefined && (
          <Text variant='body' color='destructive' role='alert'>
            {t`Couldn’t save: ${errorMessage}`}
          </Text>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={saving}>
            <Trans>Cancel</Trans>
          </AlertDialogCancel>
          <AlertDialogAction
            variant='outline'
            disabled={saving}
            onClick={discard}>
            <Trans>Discard and continue</Trans>
          </AlertDialogAction>
          <AlertDialogAction disabled={saving} onClick={save}>
            {saving ? <Trans>Saving…</Trans> : <Trans>Save and continue</Trans>}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
